import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Card,
    CardBody,
    CardGroup,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Progress,
    Row,
} from "reactstrap";
import useTabNavigation from "../../../../_hooks/useTabNavigation";
import LoadingSpinner from "../../../../_UI/Components/LoadingSpinner";
import { logout, setUpResetPassword } from "../../../../user/services/store/authentication.actions";
import AlertComponent from "../../../../_UI/Components/Alerts/AlertComponent";
import { marketplaceSetupReducerActions } from "../../../../_services/store";
import { PathwayFormGrid } from "../../../../_UI/StyledComponents/PathwayForm";
import PathwayTextInputGroup from "../../../../_UI/Components/Inputs/PathwayTextInputGroup";
import { LoadingSpinnerButton } from "../../../../_UI/Components/PathwayButtons";
import styled from "styled-components";
import {
    connectNewStripeAccountToExistingOrg,
    createNewStripeOrgForExistingUser,
    requestStripeSetupEmailResend,
    submitInitialLoginForExistingUser,
    submitNewStripeMarketplaceInstallation,
} from "../../../../user/services/store/marketplaceSetup.actions";
import StripeMarketplaceNewUserRegistrationScreen from "../components/StripeMarketplaceNewUserRegistration";
import StripeMarketplaceExistingUserLogin from "../components/StripeMarketplaceExistingUserLogin";
import useRegistrationLoadingBar from "../../../../_hooks/useRegistrationLoadingBar";
import LoginFormV2 from "../../../../_components/LoginFormV2";

const NEW_USER = "NEW_USER";
const EXISTING_USER = "EXISTING_USER";
const TABS = {
    NEW_USER,
    EXISTING_USER,
};

const TAB_LIST = () => {
    return [
        {
            description: "Registrieren",
            identifier: TABS.NEW_USER,
        },
        {
            description: "Login",
            identifier: TABS.EXISTING_USER,
        },
    ];
};

const StripeMarketplaceRegistrationScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const queryParameters = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const redirectUrl = useMemo(() => {
        const queryParameters = new URLSearchParams(location.search);
        return queryParameters.has("redirect_url") ? queryParameters.get("redirect_url") : "/";
    }, [location]);

    const marketplaceSetup = useSelector((state) => state.marketplaceSetup);
    const authentication = useSelector((state) => state.authentication);
    const userRegistration = useSelector((state) => state.userRegistration);

    const [registrationPrompt, setRegistrationPrompt] = useState("");
    const [loginSubmitted, setLoginSubmitted] = useState(false);
    const [createNewOrg, setCreateNewOrg] = useState(false);
    const [resetMail, setResetMail] = useState("");
    const [resetSuccessMessage, setResetSuccessMessage] = useState(null);
    const [forgetPasswordModal, setForgetPasswordModal] = useState(false);
    const [acceptTos, setAcceptTos] = useState(false);
    const [acceptDpa, setAcceptDpa] = useState(false);

    const knownAccount = queryParameters.get("knownAccount");
    const authDataError = queryParameters.get("authDataError");
    const liveMode = queryParameters.get("liveMode");
    const nounce = queryParameters.get("nounce");
    const sandbox = queryParameters.get("sandbox");
    const accountExists = knownAccount === "true";
    const hasConnectionError = authDataError === "true";
    const isLiveMode = liveMode === "true";
    const isSandbox = sandbox === "true";

    const [loginData, setLoginData] = useState({
        email: "",
        password: "",
        submitted: false,
        selectedOrganizationExternalUUID: "",
        nounce: nounce,
    });

    const { activeTab, setDeterminedTabs, setActiveTab } = useTabNavigation("", TABS.NEW_USER);

    useEffect(() => {
        setDeterminedTabs(TAB_LIST);
    }, [setDeterminedTabs]);

    const { progress, setProgress, statusText } = useRegistrationLoadingBar(
        marketplaceSetup.registrationLoading,
        knownAccount
    );

    useEffect(() => {
        const fetchData = async () => {
            await dispatch(logout());
        };
        fetchData().then(() => {
            if (hasConnectionError || !nounce) {
                setRegistrationPrompt(
                    "Wir konnten deinen Stripe Account leider nicht verbinden. Bitte kontaktiere uns per support@pathway-solutions.de damit unser Team einen Account für dich erstellt."
                );
                setActiveTab(TABS.EXISTING_USER);
            } else if (accountExists) {
                setRegistrationPrompt(
                    "Dieser Stripe Account ist bereits mit pathway verbunden. Bitte melde dich jetzt mit deiner E-Mail Adresse und Passwort an."
                );
                setActiveTab(TABS.EXISTING_USER);
            } else {
                setRegistrationPrompt(
                    "Willkommen bei pathway. Mit einem Klick auf die Schaltfläche unten bestätigst du die Erstellung deines neuen Kontos.\n" +
                        "\n" +
                        "Wenn du bereits ein Konto bei uns hast, kannst du deinen zusätzlichen Stripe Account verknüpfen, indem du unten auf die Schaltfläche „Login“ klickst."
                );
                setActiveTab(TABS.NEW_USER);
            }
        });
    }, [dispatch, navigate, setActiveTab, nounce, accountExists, hasConnectionError]);

    const handleSubmitInstallationRequest = async (e) => {
        e.preventDefault();
        dispatch(marketplaceSetupReducerActions.clearErrorMessages());
        try {
            await dispatch(submitNewStripeMarketplaceInstallation(nounce, isLiveMode, acceptDpa, acceptTos, isSandbox));
        } catch (error) {
            // handle error
        }
    };

    const toggleForgetPasswordModal = () => {
        setForgetPasswordModal(!forgetPasswordModal);
        setResetSuccessMessage("");
        setResetMail("");
    };

    const handleResetMailChange = (e) => {
        e.preventDefault();
        const value = e.target.value;
        setResetMail(value);
    };

    const resetPassword = async () => {
        try {
            await dispatch(setUpResetPassword(resetMail));
            setResetSuccessMessage(
                "Falls die E-Mail in unserer Datenbank vorhanden ist, werden wir einen Link zum Zurücksetzen an die von dir angegebene E-Mail schicken."
            );
            setResetMail(null);
        } catch (error) {
            // error
        }
    };

    const handleTosChange = (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setAcceptTos(value);
    };

    const handleDpaChange = (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setAcceptDpa(value);
    };

    const handleSelectOrgAndClientChange = (e) => {
        e.preventDefault();
        const { name } = e.target;
        const value = e.target.value;

        if (name === "selectedOrganizationExternalUUID") {
            if (value === "createNewOrganization") {
                setCreateNewOrg(true);
            } else setCreateNewOrg(false);
        }
        setLoginData({
            ...loginData,
            [name]: value,
        });
    };

    const handleLoginDataChange = (e) => {
        const { name } = e.target;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setLoginData({
            ...loginData,
            [name]: value,
        });
    };

    const submitInitialLogin = async () => {
        try {
            await dispatch(marketplaceSetupReducerActions.clearErrorMessages());
            setLoginSubmitted(true);
            const user = await dispatch(submitInitialLoginForExistingUser(loginData));

            if (user?.externalUUID !== marketplaceSetup.existingUserUUID) {
                dispatch(marketplaceSetupReducerActions.setUserMismatchError());
            }
        } catch (error) {
            // error
        }
    };

    const submitSelectedOrganizationAndClient = async (e) => {
        try {
            if (createNewOrg) {
                // create new org for existing user
                await handleSubmitRegistration(
                    e,
                    loginData,
                    createNewOrg,
                    isLiveMode,
                    isSandbox,
                    createNewStripeOrgForExistingUser
                );
            } else {
                // add new shop for existing org connected to user
                await handleSubmitRegistration(
                    e,
                    loginData,
                    createNewOrg,
                    isLiveMode,
                    isSandbox,
                    connectNewStripeAccountToExistingOrg
                );
            }
        } catch (error) {
            //     error
        }
    };

    const handleSubmitRegistration = async (e, registrationData, createNewOrg, isLiveMode, isSandbox, submitAction) => {
        e.preventDefault();
        dispatch(marketplaceSetupReducerActions.clearErrorMessages());
        if (
            registrationData.passwordRepeat &&
            registrationData.password &&
            registrationData.password !== registrationData.passwordRepeat
        ) {
            return dispatch(marketplaceSetupReducerActions.setPasswordMismatchError());
        }
        try {
            await dispatch(submitAction(registrationData, createNewOrg, isLiveMode, isSandbox));
            setProgress(100)
            dispatch(marketplaceSetupReducerActions.closeRegistrationModal());
            navigate("/");
        } catch (error) {
            // handle error
        }
    };

    return (
        <div className="app flex-row align-items-center">
            <Modal isOpen={forgetPasswordModal} toggle={toggleForgetPasswordModal} centered={true}>
                <ModalHeader>Passwort vergessen</ModalHeader>
                <ModalBody>
                    <PathwayFormGrid>
                        <PathwayTextInputGroup
                            label={"E-Mail"}
                            name={"resetMail"}
                            placeHolder={"E-Mail"}
                            handleChange={handleResetMailChange}
                            value={resetMail}
                            mandatory
                        />
                        <AlertComponent
                            errorMessages={authentication.resetErrorMessage}
                            successMessages={resetSuccessMessage}
                        />
                    </PathwayFormGrid>
                </ModalBody>
                <ModalFooter>
                    <LoadingSpinnerButton
                        onClick={resetPassword}
                        loading={authentication.resetLoading}
                        disabled={resetSuccessMessage || !resetMail}
                    >
                        Passwort zurücksetzen
                    </LoadingSpinnerButton>
                    <Button color="secondary" onClick={toggleForgetPasswordModal}>
                        {resetMail ? "Abbrechen" : "Schließen"}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal
                isOpen={marketplaceSetup.registrationModalOpen}
                size="lg"
                title={"Fast geschafft..."}
                primaryButtonText="Zurück"
                disabled={marketplaceSetup.registrationLoading}
            >
                <ModalHeader toggle={() => dispatch(marketplaceSetupReducerActions.closeRegistrationModal())}>
                    Fast geschafft...
                </ModalHeader>
                <ModalBody>
                    <AlertComponent
                        errorMessages={marketplaceSetup.registrationErrorMessage}
                        successMessages={marketplaceSetup.registrationStatus}
                    />
                    <AlertComponent
                        errorMessages={marketplaceSetup.emailRequestErrorMessage}
                        successMessages={marketplaceSetup.emailRequestSuccessMessage}
                    />
                    {marketplaceSetup.registrationLoading && (
                        <Progress
                            className="my-2"
                            value={progress}
                            style={{
                                height: "20px",
                            }}
                        >
                            {statusText}
                        </Progress>
                    )}
                    {!marketplaceSetup.registrationErrorMessage && !marketplaceSetup.existingUserUUID && (
                        <ButtonArea>
                            <Button
                                disabled={marketplaceSetup.registrationLoading}
                                color="primary"
                                onClick={() => navigate("/login")}
                            >
                                Login
                            </Button>
                            <LoadingSpinnerButton
                                color="secondary"
                                disabled={marketplaceSetup.registrationLoading}
                                loading={marketplaceSetup.emailRequestLoading}
                                onClick={() => dispatch(requestStripeSetupEmailResend(nounce))}
                            >
                                Keine E-Mail erhalten?
                            </LoadingSpinnerButton>
                        </ButtonArea>
                    )}
                    {!marketplaceSetup.registrationErrorMessage && marketplaceSetup.existingUserUUID && (
                        <StripeMarketplaceExistingUserLogin
                            handleChange={handleLoginDataChange}
                            loginData={loginData}
                            loginSubmitted={loginSubmitted}
                            marketplaceSetup={marketplaceSetup}
                            toggleForgetPasswordModal={toggleForgetPasswordModal}
                            submit={!loginSubmitted ? submitInitialLogin : submitSelectedOrganizationAndClient}
                            handleSelectOrgAndClientChange={handleSelectOrgAndClientChange}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Container>
                <Row className="justify-content-center">
                    <Col md="8" xs="16">
                        <CardGroup>
                            <Card>
                                <CardBody style={{ minHeight: "448px" }}>
                                    <Col xs="12" className="justify-content-center">
                                        <img
                                            src="/logo.png"
                                            alt="Pathway Logo"
                                            style={{ maxWidth: "60%" }}
                                            className="rounded mx-auto d-block"
                                        />
                                        <br />
                                        <br />
                                    </Col>
                                    {(marketplaceSetup.loading ||
                                        authentication.loading ||
                                        marketplaceSetup.autoLoginLoading) && (
                                        <LoadingContainer>
                                            <LoadingSpinner
                                                loading={
                                                    marketplaceSetup.loading ||
                                                    authentication.loading ||
                                                    marketplaceSetup.autoLoginLoading
                                                }
                                            />
                                        </LoadingContainer>
                                    )}
                                    {!marketplaceSetup.loading &&
                                        !authentication.loading &&
                                        !marketplaceSetup.autoLoginLoading &&
                                        !nounce && (
                                            <Row>
                                                <Col>
                                                    <h3>Anmeldung nicht möglich</h3>
                                                    Bei der Registrierung deines Accounts trat ein Fehler auf. <br />
                                                    Bitte installiere die App erneut.
                                                </Col>
                                            </Row>
                                        )}
                                    {!marketplaceSetup.loading &&
                                        !authentication.loading &&
                                        !marketplaceSetup.autoLoginLoading &&
                                        nounce && <AlertComponent warningMessages={registrationPrompt} />}
                                    {(authentication.errorMessage ||
                                        marketplaceSetup.registrationErrorMessage ||
                                        marketplaceSetup.loginErrorMessage ||
                                        marketplaceSetup.autoLoginErrorMessage) && (
                                        <AlertComponent
                                            errorMessages={
                                                authentication.errorMessage ||
                                                marketplaceSetup.loginErrorMessage ||
                                                marketplaceSetup.registrationErrorMessage ||
                                                marketplaceSetup.autoLoginErrorMessage
                                            }
                                        />
                                    )}
                                    {!authentication.loading && !marketplaceSetup.autoLoginLoading && nounce && (
                                        <>
                                            {activeTab === TABS.NEW_USER ? (
                                                <StripeMarketplaceNewUserRegistrationScreen
                                                    payloadSubmitted={marketplaceSetup.payloadSubmitted}
                                                    registrationLoading={marketplaceSetup.registrationLoading}
                                                    handleSubmitInstallationRequest={handleSubmitInstallationRequest}
                                                    acceptTos={acceptTos}
                                                    handleTosChange={handleTosChange}
                                                    acceptDpa={acceptDpa}
                                                    handleDpaChange={handleDpaChange}
                                                />
                                            ) : activeTab === TABS.EXISTING_USER ? (
                                                <LoginFormV2
                                                    authentication={authentication}
                                                    registration={userRegistration}
                                                    loginIdentifier="stripeMarketplace"
                                                    redirectUrl={redirectUrl}
                                                />
                                            ) : null}
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </CardGroup>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default StripeMarketplaceRegistrationScreen;

const LoadingContainer = styled.div`
    display: grid;
    justify-content: center;
    align-items: center;
`;

const ButtonArea = styled.div`
    display: grid;
    grid-template-columns: 0.5fr max-content;
    justify-content: space-between;
`;
